import { useCallback, useEffect, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { authAtom } from "../../../lib/auth-store.js";
import { apiCall } from "../../../lib/api-client.js";
import { useApiQuery } from "../../../lib/useApiQuery.js";
import {
  rolesPath,
  userByIdPath,
  usersPath,
  bootstrapPath,
} from "../../../lib/api-paths.js";
import {
  ApiError,
  type BootstrapPayload,
  type PublicRole,
  type PublicUser,
  type UsersListResponse,
} from "../../../lib/api-types.js";
import { ErrorBanner } from "../../../components/ErrorBanner.js";
import { InlineLoading } from "../../../components/Spinner.js";
import { PrimaryButton } from "../../../components/FormField.js";
import type { OnboardingStepProps } from "../types.js";
import { isAdminRole } from "../shared.js";
import { UserPickerModal } from "../UserPickerModal.js";

/**
 * Step 5 — 设置管理员 + 组织底座总览。
 * 管理员 = 把选中同事指派到 admin 角色（templateId="admin"，缺失则自动创建）。
 * 就绪判定：至少一位管理员。
 */
export function Step5Admin({
  org,
  onReadyChange,
}: OnboardingStepProps): JSX.Element {
  const auth = useAtomValue(authAtom);
  const {
    data: roles,
    loading: rolesLoading,
    refetch: refetchRoles,
  } = useApiQuery<PublicRole[]>(rolesPath(org.code), [org.code]);
  const {
    data: usersResp,
    loading: usersLoading,
    refetch: refetchUsers,
  } = useApiQuery<UsersListResponse>(
    `${usersPath(org.code)}?pageSize=200&status=active`,
    [org.code],
  );
  const { data: bootstrap, refetch: refetchBootstrap } =
    useApiQuery<BootstrapPayload>(bootstrapPath(org.code), [org.code]);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const adminRole = useMemo(
    () => (roles ?? []).find(isAdminRole) ?? null,
    [roles],
  );
  const adminRoleId = adminRole?.id ?? null;

  const adminUsers = useMemo(
    () =>
      (usersResp?.rows ?? []).filter(
        (u) => adminRoleId != null && u.roleId === adminRoleId,
      ),
    [usersResp?.rows, adminRoleId],
  );

  useEffect(() => {
    onReadyChange(adminUsers.length > 0);
  }, [adminUsers.length, onReadyChange]);

  /** 找到或创建 admin 角色，返回其 id。 */
  const ensureAdminRole = useCallback(async (): Promise<string | null> => {
    if (!auth) return null;
    if (adminRoleId) return adminRoleId;
    const created = await apiCall<PublicRole>("POST", rolesPath(org.code), {
      token: auth.token,
      body: {
        code: "admin",
        name: "管理员",
        description: "企业管理员，拥有后台管理权限",
        dosia: { templateId: "admin" },
      },
    });
    await refetchRoles();
    return created.id;
  }, [auth, adminRoleId, org.code, refetchRoles]);

  const assignAdmins = useCallback(
    async (userIds: string[]) => {
      if (!auth) return;
      setWorking(true);
      setError(null);
      try {
        const roleId = await ensureAdminRole();
        if (!roleId) throw new Error("无法创建管理员角色");
        // 新增的指派为管理员（移除不在此处理；可在用户面板改）
        const current = new Set(adminUsers.map((u) => u.id));
        const toAdd = userIds.filter((id) => !current.has(id));
        for (const id of toAdd) {
          await apiCall("PATCH", userByIdPath(org.code, id), {
            token: auth.token,
            body: { roleId },
          });
        }
        await Promise.all([refetchUsers(), refetchBootstrap()]);
        setPickerOpen(false);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : String(err));
      } finally {
        setWorking(false);
      }
    },
    [auth, ensureAdminRole, adminUsers, org.code, refetchUsers, refetchBootstrap],
  );

  if (rolesLoading || usersLoading) {
    return <InlineLoading label="加载管理员信息…" />;
  }

  const nameOf = (u: PublicUser): string => u.feishu?.name ?? u.user;
  const rolesWithLeaders = bootstrap
    ? Object.keys(bootstrap.leadershipMap).length
    : 0;

  return (
    <div className="space-y-5">
      {error ? (
        <ErrorBanner onDismiss={() => setError(null)}>{error}</ErrorBanner>
      ) : null}

      {/* 管理员设置 */}
      <section className="rounded-lg border border-dosia-border bg-dosia-bg-light p-4">
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-dosia-text">企业管理员</h4>
          <PrimaryButton onClick={() => setPickerOpen(true)} disabled={working}>
            选择管理员
          </PrimaryButton>
        </div>
        {adminUsers.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {adminUsers.map((u) => (
              <span
                key={u.id}
                className="rounded-full bg-dosia-accent/15 px-2.5 py-1 text-xs text-dosia-text"
              >
                {nameOf(u)}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-xs text-amber-700">
            还没有管理员。请至少指定一位（通常是你自己或核心负责人）。
          </p>
        )}
      </section>

      {/* 组织底座总览 */}
      {bootstrap ? (
        <section>
          <h4 className="mb-2 text-sm font-semibold text-dosia-text">
            组织底座总览
          </h4>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <SummaryCard label="员工" count={bootstrap.employees.length} />
            <SummaryCard label="角色 / 部门" count={bootstrap.roles.length} />
            <SummaryCard label="已配负责人的角色" count={rolesWithLeaders} />
            <SummaryCard label="管理员" count={adminUsers.length} />
          </div>
          <p className="mt-3 text-xs text-dosia-text-dim">
            点「完成搭建」后，这套组织底座即生效。之后入职、离职会通过飞书自动同步，你也可随时回来调整。
          </p>
        </section>
      ) : null}

      {pickerOpen ? (
        <UserPickerModal
          title="选择企业管理员"
          orgCode={org.code}
          initial={adminUsers.map((u) => u.id)}
          saving={working}
          onCancel={() => setPickerOpen(false)}
          onSave={assignAdmins}
        />
      ) : null}
    </div>
  );
}

function SummaryCard({
  label,
  count,
}: {
  label: string;
  count: number;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-dosia-border bg-dosia-bg-light p-3 text-center">
      <p className="text-2xl font-semibold text-dosia-text">{count}</p>
      <p className="mt-0.5 text-xs text-dosia-text-secondary">{label}</p>
    </div>
  );
}
